import axios from 'axios';
import { Capacitor } from '@capacitor/core';

const PROD_API_URL = 'https://app.fopspro.com/api';

const getBaseURL = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;

  const hostname = window.location.hostname;
  
  // Use professional domain if not on localhost/LAN
  if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.startsWith('192.168.')) {
    return PROD_API_URL;
  }

  // If running natively (Android/iOS APK)
  if (Capacitor.isNativePlatform()) {
    return PROD_API_URL; 
  }

  // Local development fallback
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/api';
  }

  // Local LAN access (e.g. mobile device on same Wi-Fi)
  if (hostname.startsWith('192.168.')) {
    return `http://${hostname}:5045/api`;
  }

  return '/api';
};

const API = axios.create({ baseURL: getBaseURL(), timeout: 30000 });

export const getMediaURL = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  
  // Ensure uploads are routed through the API proxy
  let normalizedPath = path;
  if (path.startsWith('/uploads')) {
    normalizedPath = `/api${path}`;
  }
  
  const baseURL = getBaseURL().replace('/api', '');
  return `${baseURL}${normalizedPath}`;
};

// Add interceptor to help debug the full URL
API.interceptors.request.use(config => {
  const token = localStorage.getItem('fieldops_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Debug logic: Store full URL for error reporting
  config.fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
  return config;
});

API.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('fieldops_token');
      localStorage.removeItem('fieldops_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const authAPI = {
  login: (data) => API.post('/auth/login', data),
  me: () => API.get('/auth/me'),
  changePassword: (data) => API.post('/auth/change-password', data),
};

// Users
export const usersAPI = {
  list: (params) => API.get('/users', { params }),
  technicians: () => API.get('/users/technicians'),
  get: (id) => API.get(`/users/${id}`),
  create: (data) => API.post('/users', data),
  update: (id, data) => API.put(`/users/${id}`, data),
  getPermissionsMatrix: () => API.get('/users/permissions/matrix'),
  togglePermission: (data) => API.post('/users/permissions/toggle', data)
};

// Assets
export const assetsAPI = {
  list: (params) => API.get('/assets', { params }),
  get: (id) => API.get(`/assets/${id}`),
  getByQR: (qrCode) => API.get(`/assets/qr/${qrCode}`),
  create: (data) => API.post('/assets', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => API.put(`/assets/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => API.delete(`/assets/${id}`),
  clone: (id) => API.post(`/assets/${id}/clone`),
  export: (params) => API.get('/assets/export', { params, responseType: 'blob' }),
  getQRImage: (id) => API.get(`/assets/${id}/qr-image`),
  categories: () => API.get('/assets/categories'),
  createCategory: (data) => API.post('/assets/categories', data),
  import: (data) => API.post('/assets/import', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getTemplate: () => API.get('/assets/template', { responseType: 'blob' }),
  regenerateQR: (id) => API.post(`/assets/${id}/regenerate-qr`),
  getHistory: (id) => API.get(`/assets/${id}/history`),
  addHistory: (id, data) => API.post(`/assets/${id}/history`, data),
};

// Taxonomy
export const taxonomyAPI = {
  natures: () => API.get('/taxonomy/natures'),
  createNature: (data) => API.post('/taxonomy/natures', data),
  categories: (params) => API.get('/taxonomy/categories', { params }),
  types: (params) => API.get('/taxonomy/types', { params }),
  createType: (data) => API.post('/taxonomy/types', data),
};

// Workflows
export const workflowsAPI = {
  list: (params) => API.get('/workflows', { params }),
  get: (id) => API.get(`/workflows/${id}`),
  create: (data) => API.post('/workflows', data),
  update: (id, data) => API.put(`/workflows/${id}`, data),
  delete: (id) => API.delete(`/workflows/${id}`),
  uploadMaterial: (data) => API.post('/workflows/upload-material', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Jobs
export const jobsAPI = {
  list: (params) => API.get('/jobs', { params }),
  myJobs: () => API.get('/jobs/my'),
  get: (id) => API.get(`/jobs/${id}`),
  create: (data) => API.post('/jobs', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => API.put(`/jobs/${id}`, data),
  start: (id) => API.patch(`/jobs/${id}/start`),
  complete: (id, data) => API.patch(`/jobs/${id}/complete`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  comment: (id, data) => API.post(`/jobs/${id}/comment`, data),
  addAttachments: (id, data) => API.post(`/jobs/${id}/attachments`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => API.delete(`/jobs/${id}`),
  clone: (id) => API.post(`/jobs/${id}/clone`),
};

// Dashboard
export const dashboardAPI = {
  stats: () => API.get('/dashboard/stats'),
};

// Reports
export const reportsAPI = {
  fetchPDF: (id) => API.get(`/reports/job/${id}/pdf`, { responseType: 'blob' }),
  downloadPDF: async (id) => {
    const res = await API.get(`/reports/job/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  },
  emailReport: (id, data) => API.post(`/reports/job/${id}/email`, data),
};

// Schedules
export const schedulesAPI = {
  list: () => API.get('/schedules'),
  create: (data) => API.post('/schedules', data),
  delete: (id) => API.delete(`/schedules/${id}`),
  forecast: () => API.get('/schedules/forecast'),
};

// Locations
export const locationsAPI = {
  sites: () => API.get('/locations/sites'),
  createSite: (data) => API.post('/locations/sites', data),
  siteLocations: (siteId) => API.get(`/locations/sites/${siteId}/locations`),
  createLocation: (data) => API.post('/locations', data),
};

// Notifications
export const notificationsAPI = {
  list: () => API.get('/notifications'),
  markRead: (id) => API.patch(`/notifications/${id}/read`),
  markAllRead: () => API.patch('/notifications/read-all'),
};

// SOPs
export const sopsAPI = {
  list: () => API.get('/sops'),
  getForAsset: (assetId) => API.get(`/sops/asset/${assetId}`),
  create: (data) => API.post('/sops', data),
  update: (id, data) => API.put(`/sops/${id}`, data),
  delete: (id) => API.delete(`/sops/${id}`),
  link: (data) => API.post('/sops/link', data),
};

// Analytics
export const analyticsAPI = {
  get: (params) => API.get('/analytics', { params }),
};

// System
export const systemAPI = {
  auditLogs: (params) => API.get('/system/audit-logs', { params }),
  track: (data) => API.post('/system/track', data),
  settings: () => API.get('/system/settings'),
  updateSettings: (data) => API.put('/system/settings', data),
};

export default API;
